import type { BoxProps } from '@talos/kyoko';
import {
  Box,
  CurrentDocumentProvider,
  DROPDOWN_PORTAL_ID,
  getLoadingAnimation,
  MODAL_PORTAL_ID,
  POPOVER_PORTAL_ID,
  PortalContextProvider,
  useIsIntersecting,
  usePortal,
} from '@talos/kyoko';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { LayoutPanelApi } from '../../AppLayout/types';
import { ErrorBoundary } from '../../ErrorBoundary';
import { useLayoutContext } from '../providers/LayoutContextProvider';
import { PanelContextProvider } from '../providers/PanelContextProvider';

interface PanelProps extends BoxProps {
  panelApi: LayoutPanelApi;
}

export function Panel(props: PanelProps) {
  const { panelApi } = props;
  const { hiddenPanelIds } = useLayoutContext();

  // Trigger a re-render of the panel if the panel is in a popout window
  const [isPopout, setIsPopout] = useState(false);
  useEffect(() => {
    const disposable = panelApi.onDidLocationChange(() => {
      setIsPopout(panelApi.location.type === 'popout');
    });
    return () => disposable.dispose();
  }, [panelApi]);
  // Get the current document if the panel is in a popout window
  const currentWindow = useMemo(
    () => (isPopout && panelApi.location.type === 'popout' ? panelApi.location.getWindow() : window),
    [panelApi, isPopout]
  );
  const currentDocument = currentWindow.document;

  const panelContext = useMemo(() => {
    return {
      isPopout,
    };
  }, [isPopout]);

  if (hiddenPanelIds.has(panelApi.id)) {
    return null;
  }

  return (
    <ErrorBoundary background="backgroundContent">
      <PanelContextProvider value={panelContext}>
        <CurrentDocumentProvider currentDocument={currentDocument} currentWindow={currentWindow}>
          <PortalContextProvider>
            <InnerPanel {...props} />
          </PortalContextProvider>
        </CurrentDocumentProvider>
      </PanelContextProvider>
    </ErrorBoundary>
  );
}

/** Wrapper component to make sure only visible content is rendered */
function InnerPanel({ children, ...props }: PanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const isIntersecting = useIsIntersecting(panelRef);
  const { setPortalRef: modalPortalRef } = usePortal(MODAL_PORTAL_ID);
  const { setPortalRef: dropdownPortalRef } = usePortal(DROPDOWN_PORTAL_ID);
  const { setPortalRef: popoverPortalRef } = usePortal(POPOVER_PORTAL_ID);
  const isPopout = props.panelApi.location.type === 'popout';

  return (
    <Box data-testid="layout-panel" ref={panelRef} h="100%" background="backgroundContent" overflow="hidden" {...props}>
      {isPopout && (
        <>
          <Box ref={modalPortalRef} />
          <Box ref={popoverPortalRef} />
          <Box ref={dropdownPortalRef} />
        </>
      )}
      {panelRef.current && (isIntersecting ? children : <LoadingPanel datatest-id="loading-panel" />)}
    </Box>
  );
}

const LoadingPanel = styled(Box)`
  ${({ theme }) => getLoadingAnimation(theme, true, 'backgroundContent', 'gray.010')}
  height: 100%;
  width: 100%;
`;
